import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  FileText, Package, Users, Clock, CheckCircle, ArrowRight, 
  RefreshCw, Search, Eye, Edit, ChevronDown, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmployeePortal = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const isSpanish = i18n.language === 'es';
  
  const [stats, setStats] = useState({
    total_quotes: 0,
    pending_quotes: 0,
    reviewing_quotes: 0,
    total_shipments: 0,
    active_shipments: 0,
    total_clients: 0
  });
  const [quotes, setQuotes] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [statusLocation, setStatusLocation] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch stats - using credentials for httpOnly cookie auth
      const statsRes = await fetch(`${API_URL}/api/employee/dashboard-stats`, { 
        credentials: 'include' 
      });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
      
      // Fetch quotes
      const quotesRes = await fetch(`${API_URL}/api/employee/quotes?limit=100`, { 
        credentials: 'include' 
      });
      if (quotesRes.ok) {
        const quotesData = await quotesRes.json();
        setQuotes(quotesData);
      }
      
      // Fetch shipments
      const shipmentsRes = await fetch(`${API_URL}/api/employee/shipments?limit=100`, { 
        credentials: 'include' 
      });
      if (shipmentsRes.ok) {
        const shipmentsData = await shipmentsRes.json();
        setShipments(shipmentsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(isSpanish ? 'Error al cargar datos' : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  // Wait for auth context to initialize
  if (authLoading) {
    return (
      <div className="min-h-screen bg-steel-50 pt-24 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  // Redirect if not authenticated or not employee/admin
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (user?.role !== 'employee' && user?.role !== 'admin') {
    return <Navigate to="/client-portal" replace />;
  }

  const updateQuoteStatus = async () => {
    if (!selectedQuote || !newStatus) return;
    
    try {
      const res = await fetch(`${API_URL}/api/employee/quotes/${selectedQuote.quote_number}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        toast.success(isSpanish ? 'Estado actualizado' : 'Status updated');
        setSelectedQuote(null);
        setNewStatus('');
        fetchData();
      } else {
        toast.error(isSpanish ? 'Error al actualizar' : 'Update failed');
      }
    } catch (error) {
      toast.error(isSpanish ? 'Error de conexión' : 'Connection error');
    }
  };

  const updateShipmentStatus = async () => {
    if (!selectedShipment || !newStatus) return;
    
    try {
      const res = await fetch(`${API_URL}/api/employee/shipments/${selectedShipment.tracking_number}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          status: newStatus,
          location: statusLocation,
          notes: statusNotes
        })
      });
      
      if (res.ok) {
        toast.success(isSpanish ? 'Estado actualizado' : 'Status updated');
        setSelectedShipment(null);
        setNewStatus('');
        setStatusLocation('');
        setStatusNotes('');
        fetchData();
      } else {
        toast.error(isSpanish ? 'Error al actualizar' : 'Update failed');
      }
    } catch (error) {
      toast.error(isSpanish ? 'Error de conexión' : 'Connection error');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      reviewing: 'bg-blue-100 text-blue-800',
      quoted: 'bg-purple-100 text-purple-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      confirmed: 'bg-blue-100 text-blue-800',
      'in-transit': 'bg-purple-100 text-purple-800',
      customs: 'bg-orange-100 text-orange-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString(isSpanish ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredQuotes = quotes.filter(q => {
    const matchesSearch = q.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.contact_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || q.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredShipments = shipments.filter(s => {
    const matchesSearch = s.tracking_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.origin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.destination?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const quoteStatuses = ['pending', 'reviewing', 'quoted', 'accepted', 'rejected', 'expired'];
  const shipmentStatuses = ['pending', 'confirmed', 'in-transit', 'customs', 'delivered', 'cancelled'];

  return (
    <div className="min-h-screen bg-steel-50 pt-24" data-testid="employee-portal">
      {/* Header */}
      <section className="bg-navy-900 py-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          >
            <div>
              <h1 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-2">
                {isSpanish ? 'Portal del Empleado' : 'Employee Portal'}
              </h1>
              <p className="text-steel-300">
                {isSpanish 
                  ? `Gestión de cotizaciones y envíos`
                  : `Quotes and shipments management`}
              </p>
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10"
                onClick={fetchData}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {isSpanish ? 'Actualizar' : 'Refresh'}
              </Button>
              {user?.role === 'admin' && (
                <Link to="/admin">
                  <Button className="btn-accent">
                    {isSpanish ? 'Panel Admin' : 'Admin Panel'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Cards */}
      <section className="py-8 -mt-8">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { label: isSpanish ? 'Total Cotizaciones' : 'Total Quotes', value: stats.total_quotes, icon: FileText, color: 'bg-blue-500' },
              { label: isSpanish ? 'Pendientes' : 'Pending', value: stats.pending_quotes, icon: Clock, color: 'bg-yellow-500' },
              { label: isSpanish ? 'En Revisión' : 'Reviewing', value: stats.reviewing_quotes, icon: AlertCircle, color: 'bg-orange-500' },
              { label: isSpanish ? 'Total Envíos' : 'Total Shipments', value: stats.total_shipments, icon: Package, color: 'bg-purple-500' },
              { label: isSpanish ? 'Envíos Activos' : 'Active Shipments', value: stats.active_shipments, icon: CheckCircle, color: 'bg-green-500' },
              { label: isSpanish ? 'Total Clientes' : 'Total Clients', value: stats.total_clients, icon: Users, color: 'bg-indigo-500' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center`}>
                        <stat.icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-navy-800">{stat.value}</p>
                        <p className="text-xs text-steel-600">{stat.label}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tabs and Content */}
      <section className="py-8">
        <div className="container-custom">
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { id: 'overview', label: isSpanish ? 'Resumen' : 'Overview' },
              { id: 'quotes', label: isSpanish ? 'Cotizaciones' : 'Quotes' },
              { id: 'shipments', label: isSpanish ? 'Envíos' : 'Shipments' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setStatusFilter('all'); }}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-navy-800 text-white'
                    : 'bg-white text-steel-600 hover:bg-steel-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Filters */}
          {activeTab !== 'overview' && (
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-steel-400" />
                <Input
                  placeholder={isSpanish ? 'Buscar por número, cliente...' : 'Search by number, client...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder={isSpanish ? 'Estado' : 'Status'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isSpanish ? 'Todos' : 'All'}</SelectItem>
                  {(activeTab === 'quotes' ? quoteStatuses : shipmentStatuses).map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-gold animate-spin mx-auto mb-4" />
              <p className="text-steel-600">{isSpanish ? 'Cargando...' : 'Loading...'}</p>
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Recent Quotes */}
                  <Card className="border-0 shadow-lg">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>{isSpanish ? 'Cotizaciones Recientes' : 'Recent Quotes'}</span>
                        <button 
                          onClick={() => setActiveTab('quotes')}
                          className="text-sm text-gold hover:underline"
                        >
                          {isSpanish ? 'Ver todas' : 'View all'}
                        </button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {quotes.length === 0 ? (
                        <p className="text-steel-500 text-center py-4">
                          {isSpanish ? 'No hay cotizaciones' : 'No quotes'}
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {quotes.slice(0, 5).map(quote => (
                            <div key={quote.quote_number} className="flex items-center justify-between p-3 bg-steel-50 rounded-lg">
                              <div>
                                <p className="font-medium text-navy-800">{quote.quote_number}</p>
                                <p className="text-sm text-steel-600">{quote.contact_name} - {quote.service_type}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(quote.status)}`}>
                                  {quote.status}
                                </span>
                                <button 
                                  onClick={() => { setSelectedQuote(quote); setNewStatus(quote.status); }}
                                  className="text-steel-400 hover:text-gold"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Recent Shipments */}
                  <Card className="border-0 shadow-lg">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>{isSpanish ? 'Envíos Recientes' : 'Recent Shipments'}</span>
                        <button 
                          onClick={() => setActiveTab('shipments')}
                          className="text-sm text-gold hover:underline"
                        >
                          {isSpanish ? 'Ver todos' : 'View all'}
                        </button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {shipments.length === 0 ? (
                        <p className="text-steel-500 text-center py-4">
                          {isSpanish ? 'No hay envíos' : 'No shipments'}
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {shipments.slice(0, 5).map(shipment => (
                            <div key={shipment.tracking_number} className="flex items-center justify-between p-3 bg-steel-50 rounded-lg">
                              <div>
                                <p className="font-medium text-navy-800">{shipment.tracking_number}</p>
                                <p className="text-sm text-steel-600">{shipment.origin} → {shipment.destination}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(shipment.status)}`}>
                                  {shipment.status}
                                </span>
                                <button 
                                  onClick={() => { setSelectedShipment(shipment); setNewStatus(shipment.status); }}
                                  className="text-steel-400 hover:text-gold"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Quotes Tab */}
              {activeTab === 'quotes' && (
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-0">
                    {filteredQuotes.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText className="w-12 h-12 text-steel-300 mx-auto mb-4" />
                        <p className="text-steel-600">
                          {isSpanish ? 'No se encontraron cotizaciones' : 'No quotes found'}
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-steel-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-steel-600 uppercase">#</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-steel-600 uppercase">{isSpanish ? 'Cliente' : 'Client'}</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-steel-600 uppercase">{isSpanish ? 'Servicio' : 'Service'}</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-steel-600 uppercase">{isSpanish ? 'Ruta' : 'Route'}</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-steel-600 uppercase">{isSpanish ? 'Fecha' : 'Date'}</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-steel-600 uppercase">{isSpanish ? 'Estado' : 'Status'}</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-steel-600 uppercase"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-steel-200">
                            {filteredQuotes.map(quote => (
                              <tr key={quote.quote_number} className="hover:bg-steel-50">
                                <td className="px-4 py-3 font-medium text-navy-800">{quote.quote_number}</td>
                                <td className="px-4 py-3">
                                  <div>
                                    <p className="text-sm font-medium text-navy-800">{quote.contact_name}</p>
                                    <p className="text-xs text-steel-500">{quote.contact_email}</p>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-steel-600">{quote.service_type}</td>
                                <td className="px-4 py-3 text-sm text-steel-600">
                                  {quote.origin_country} → {quote.destination_country}
                                </td>
                                <td className="px-4 py-3 text-sm text-steel-600">{formatDate(quote.created_at)}</td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(quote.status)}`}>
                                    {quote.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <button 
                                    onClick={() => { setSelectedQuote(quote); setNewStatus(quote.status); }}
                                    className="text-gold hover:text-gold/80"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Shipments Tab */}
              {activeTab === 'shipments' && (
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-0">
                    {filteredShipments.length === 0 ? (
                      <div className="text-center py-12">
                        <Package className="w-12 h-12 text-steel-300 mx-auto mb-4" />
                        <p className="text-steel-600">
                          {isSpanish ? 'No se encontraron envíos' : 'No shipments found'}
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-steel-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-steel-600 uppercase">{isSpanish ? 'Tracking' : 'Tracking'}</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-steel-600 uppercase">{isSpanish ? 'Cliente' : 'Client'}</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-steel-600 uppercase">{isSpanish ? 'Origen' : 'Origin'}</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-steel-600 uppercase">{isSpanish ? 'Destino' : 'Destination'}</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-steel-600 uppercase">{isSpanish ? 'Fecha' : 'Date'}</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-steel-600 uppercase">{isSpanish ? 'Estado' : 'Status'}</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-steel-600 uppercase"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-steel-200">
                            {filteredShipments.map(shipment => (
                              <tr key={shipment.tracking_number} className="hover:bg-steel-50">
                                <td className="px-4 py-3 font-medium text-navy-800">{shipment.tracking_number}</td>
                                <td className="px-4 py-3 text-sm text-steel-600">{shipment.client_name || '-'}</td>
                                <td className="px-4 py-3 text-sm text-steel-600">{shipment.origin}</td>
                                <td className="px-4 py-3 text-sm text-steel-600">{shipment.destination}</td>
                                <td className="px-4 py-3 text-sm text-steel-600">{formatDate(shipment.created_at)}</td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(shipment.status)}`}>
                                    {shipment.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <button 
                                    onClick={() => { setSelectedShipment(shipment); setNewStatus(shipment.status); }}
                                    className="text-gold hover:text-gold/80"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </section>

      {/* Quote Status Dialog */}
      <Dialog open={!!selectedQuote} onOpenChange={() => setSelectedQuote(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isSpanish ? 'Actualizar Estado de Cotización' : 'Update Quote Status'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm text-steel-600 mb-1">{isSpanish ? 'Cotización' : 'Quote'}</p>
              <p className="font-medium">{selectedQuote?.quote_number}</p>
            </div>
            <div>
              <label className="text-sm text-steel-600 mb-1 block">{isSpanish ? 'Nuevo Estado' : 'New Status'}</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {quoteStatuses.map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedQuote(null)}>
              {isSpanish ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={updateQuoteStatus} className="btn-accent">
              {isSpanish ? 'Actualizar' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shipment Status Dialog */}
      <Dialog open={!!selectedShipment} onOpenChange={() => setSelectedShipment(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isSpanish ? 'Actualizar Estado de Envío' : 'Update Shipment Status'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm text-steel-600 mb-1">{isSpanish ? 'Tracking' : 'Tracking'}</p>
              <p className="font-medium">{selectedShipment?.tracking_number}</p>
            </div>
            <div>
              <label className="text-sm text-steel-600 mb-1 block">{isSpanish ? 'Nuevo Estado' : 'New Status'}</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {shipmentStatuses.map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-steel-600 mb-1 block">{isSpanish ? 'Ubicación' : 'Location'}</label>
              <Input 
                value={statusLocation}
                onChange={(e) => setStatusLocation(e.target.value)}
                placeholder={isSpanish ? 'Ubicación actual' : 'Current location'}
              />
            </div>
            <div>
              <label className="text-sm text-steel-600 mb-1 block">{isSpanish ? 'Notas' : 'Notes'}</label>
              <Input 
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                placeholder={isSpanish ? 'Notas adicionales' : 'Additional notes'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedShipment(null)}>
              {isSpanish ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={updateShipmentStatus} className="btn-accent">
              {isSpanish ? 'Actualizar' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeePortal;
